import { BUILDING_DEFINITIONS, TICK_INTERVAL_SECONDS, DEFAULT_RESOURCE_RATES, DEFAULT_STORAGE_CAPACITY, OZONE_MAX, OZONE_CRITICAL_LEVEL, OZONE_REDUCTION_PER_LEVEL_PER_DAY, PHYSICS_BONUS_PER_LEVEL, PHYSICS_MAX_LEVEL, ECOLOGY_RECOVERY_PER_LEVEL_PER_DAY, ECOLOGY_MAX_LEVEL, SYSTEM_POSITION_MAX, POSITION_BASE_SATELLITE_ENERGY, DEFAULT_SUN_AGE_DAYS, SUN_AGE_MIN, SUN_AGE_MAX_DAYS, SYSTEMS_PER_GALAXY, REFUND_RATES, SOLAR_SATELLITE_SHIP_ID, RESOURCE_PURCHASE_DAILY_LIMIT, RESOURCE_PURCHASE_INCOME_HOURS, } from '@kaeliux/shared';
import { growByBreakpoints } from './buildings.js';
import { QueueError } from './tickEvents.js';
export { TICK_INTERVAL_SECONDS, DEFAULT_RESOURCE_RATES, DEFAULT_STORAGE_CAPACITY, OZONE_MAX, OZONE_CRITICAL_LEVEL, OZONE_REDUCTION_PER_LEVEL_PER_DAY, PHYSICS_BONUS_PER_LEVEL, PHYSICS_MAX_LEVEL, ECOLOGY_RECOVERY_PER_LEVEL_PER_DAY, ECOLOGY_MAX_LEVEL, SYSTEM_POSITION_MAX, POSITION_BASE_SATELLITE_ENERGY, DEFAULT_SUN_AGE_DAYS, SUN_AGE_MIN, SUN_AGE_MAX_DAYS, SYSTEMS_PER_GALAXY, REFUND_RATES, };
/**
 * Nominal refund for a cost at independent economic/Energy rates — floors
 * each resource independently, same rounding {@link applyResourceRefundAtRates}
 * applies to the actual credit. Shared by the eventLog's informational
 * `refund` field (cancellation) and by clients previewing a refund before
 * committing to an action (e.g. a building-destruction confirmation dialog)
 * without duplicating the floor/rate logic.
 */
export function calculateNominalRefundAtRates(cost, economicRate, energyRate) {
    const refund = {};
    if (cost.metal)
        refund.metal = Math.floor(cost.metal * economicRate);
    if (cost.minerals)
        refund.minerals = Math.floor(cost.minerals * economicRate);
    if (cost.gas)
        refund.gas = Math.floor(cost.gas * economicRate);
    if (cost.energy)
        refund.energy = Math.floor(cost.energy * energyRate);
    return refund;
}
/** Nominal refund for a cost at one uniform rate — see {@link calculateNominalRefundAtRates}. */
export function calculateNominalRefund(cost, refundType) {
    const rate = REFUND_RATES[refundType];
    return calculateNominalRefundAtRates(cost, rate, rate);
}
// ---------------------------------------------------------------------------
// Universe initialisation
// ---------------------------------------------------------------------------
/**
 * Generate initial sun ages for a new universe.
 * Each system gets a random age in [DEFAULT_SUN_AGE_DAYS, SUN_AGE_MAX_DAYS].
 * Index 0 in each galaxy array is always 0 (no system at position 0).
 *
 * `random` is required (no `Math.random` default) — game-engine must stay
 * side-effect-free and deterministic for the same inputs (see CLAUDE.md);
 * the caller supplies `Math.random` at the call site (issue #101).
 */
export function generateSunAges(numGalaxies, numSystems, random) {
    const result = {};
    for (let g = 1; g <= numGalaxies; g++) {
        result[`g${g}`] = Array.from({ length: numSystems + 1 }, (_, i) => i === 0 ? 0 : Math.floor(DEFAULT_SUN_AGE_DAYS + random() * 10));
    }
    return result;
}
// ---------------------------------------------------------------------------
// Ozone
// ---------------------------------------------------------------------------
export function calculateOzoneLoss(buildings, seconds = TICK_INTERVAL_SECONDS) {
    const totalLevels = Object.values(buildings).reduce((sum, b) => sum + (b.level || 0), 0);
    if (totalLevels <= 0)
        return 0;
    return (totalLevels * OZONE_REDUCTION_PER_LEVEL_PER_DAY * seconds) / 86400;
}
/**
 * Ecology science recovery — 250 Ozone/day per level, capped at level 15
 * (3750/day).
 */
export function calculateOzoneRecovery(ecologyLevel, seconds = TICK_INTERVAL_SECONDS) {
    const level = Math.min(Math.max(Math.floor(ecologyLevel), 0), ECOLOGY_MAX_LEVEL);
    if (level <= 0)
        return 0;
    return (level * ECOLOGY_RECOVERY_PER_LEVEL_PER_DAY * seconds) / 86400;
}
/**
 * Net Ozone change over `seconds`: Ecology recovery minus building-driven
 * loss. Can be positive (net recovery) or negative (net loss) — the caller
 * clamps the running total to `[0, OZONE_MAX]`.
 */
export function calculateOzoneChange(buildings, ecologyLevel, seconds = TICK_INTERVAL_SECONDS) {
    return calculateOzoneRecovery(ecologyLevel, seconds) - calculateOzoneLoss(buildings, seconds);
}
/**
 * A planet is ozone-locked when its current Ozone is below the critical
 * threshold. Single condition, no hysteresis — the same value is both the
 * lock and unlock threshold (see `docs/game-details/planet-lock-mechanism.md`).
 */
export function isPlanetOzoneLocked(planet) {
    return planet.state.ozone.current < OZONE_CRITICAL_LEVEL;
}
/**
 * Days until Ozone reaches the critical threshold if `netChangePerDay` (see
 * {@link calculateOzoneChange}, called with `seconds = 86_400`) holds
 * constant. Returns `null` when the net change is not positive — Ozone
 * won't recover on its own, so there is no ETA to show. Returns `0` when
 * already at or above the threshold.
 */
export function estimateOzoneUnlockDays(currentOzone, netChangePerDay) {
    if (currentOzone >= OZONE_CRITICAL_LEVEL)
        return 0;
    if (netChangePerDay <= 0)
        return null;
    return (OZONE_CRITICAL_LEVEL - currentOzone) / netChangePerDay;
}
// ---------------------------------------------------------------------------
// Energy — constant, computed resource (not income-based)
// ---------------------------------------------------------------------------
/**
 * Ozone multiplier for energy calculation (piecewise linear).
 * 15000→12000: 1.0–1.5 | 12000→9000: 1.5–2.0 | 9000→6000: 2.0–2.5
 * 6000→3000: 2.5–3.5   | 3000→150: 3.5–5.0
 */
export function calculateOzoneMultiplier(currentOzone) {
    const segments = [
        { high: OZONE_MAX * 1.0, low: OZONE_MAX * 0.8, multHigh: 1.0, multLow: 1.2 },
        { high: OZONE_MAX * 0.8, low: OZONE_MAX * 0.6, multHigh: 1.2, multLow: 1.5 },
        { high: OZONE_MAX * 0.6, low: OZONE_MAX * 0.4, multHigh: 1.5, multLow: 2.0 },
        { high: OZONE_MAX * 0.4, low: OZONE_MAX * 0.2, multHigh: 2.0, multLow: 3.0 },
        { high: OZONE_MAX * 0.2, low: OZONE_CRITICAL_LEVEL, multHigh: 3.0, multLow: 5.0 },
    ];
    if (currentOzone >= OZONE_MAX)
        return 1.0;
    if (currentOzone <= OZONE_CRITICAL_LEVEL)
        return 5.0;
    for (const { high, low, multHigh, multLow } of segments) {
        if (currentOzone >= low && currentOzone <= high) {
            const t = (currentOzone - low) / (high - low);
            return multLow + t * (multHigh - multLow);
        }
    }
    return 5.0;
}
/**
 * Solar proximity multiplier based on planet position in its system.
 * Position 1 (closest) → 1.0; position 15 (farthest) → 0.5.
 * Linear: 1 − (position − 1) / (2 × (MAX − 1))
 *
 * @deprecated Superseded by {@link calculateProximityMultiplierV2}
 * (issue #4): systems have 24 positions, not 15, and the docs' energy
 * table is tiered per position, not a linear falloff. The 15-position
 * constant is frozen inline (NOT `SYSTEM_POSITION_MAX`, which is now 24)
 * so this version's past behavior stays reconstructable.
 */
export function calculateProximityMultiplier(position) {
    const LEGACY_SYSTEM_POSITION_MAX = 15;
    return 1 - (position - 1) / (2 * (LEGACY_SYSTEM_POSITION_MAX - 1));
}
/**
 * Solar proximity multiplier V2 — per-position lookup from the Satellite
 * energy table in `docs/game-details/planet.md` (positions 1–24, tiered
 * 100/90/85), normalized so position 1 → 1.0. The top-tier positions
 * 1-8 are will get 100% sun energy; the farthest position 24 sits on the
 * 85% tier, not a linear falloff — the old formula that used to be in resources.md
 * produced an absurd values (~0.18) at position 24.
 * Out-of-range positions clamp into [1, SYSTEM_POSITION_MAX].
 */
export function calculateProximityMultiplierV2(position) {
    const clamped = Math.min(Math.max(Math.floor(position), 1), SYSTEM_POSITION_MAX);
    const energy = POSITION_BASE_SATELLITE_ENERGY[clamped] ?? POSITION_BASE_SATELLITE_ENERGY[1];
    return energy / POSITION_BASE_SATELLITE_ENERGY[1];
}
/**
 * Physics science additive multiplier.
 * +5% per level, maximum +50% at level 10.
 */
export function calculatePhysicsMultiplier(physicsLevel) {
    const level = Math.min(Math.max(Math.floor(physicsLevel), 0), PHYSICS_MAX_LEVEL);
    return 1 + level * PHYSICS_BONUS_PER_LEVEL;
}
/**
 * Base energy of the planet before buildings and satellites.
 * planet_base_energy = sun_age_days × proximity_multiplier × ozone_multiplier
 *
 * Uses the V2 (table-based, 24-position) proximity multiplier since
 * issue #4. Position-1 planets are unaffected (multiplier 1.0 in both
 * versions).
 */
export function calculatePlanetBaseEnergy(sunAgeDays, position, currentOzone) {
    return sunAgeDays
        * calculateProximityMultiplierV2(position)
        * calculateOzoneMultiplier(currentOzone);
}
// ---------------------------------------------------------------------------
// Main tick
// ---------------------------------------------------------------------------
export function calculatePlanetTick(planet, seconds = TICK_INTERVAL_SECONDS, resourceMultiplier = 1, context = {}) {
    const { sunAgeDays = DEFAULT_SUN_AGE_DAYS, physicsLevel = 0, ecologyLevel = 0, now = new Date(), economyMultiplier = 1, ozoneExempt = false, } = context;
    // Ozone-locked planets produce nothing this tick — checked against the
    // Ozone level BEFORE this tick's own ozone change is applied below, so a
    // tick that recovers Ozone past the critical level resumes production
    // starting next tick, not retroactively within itself. Ozone accrual
    // itself is never gated: Ecology recovery must keep running even while
    // locked, or a locked planet could never unlock. A Team planet
    // (`ozoneExempt`) is simply never locked.
    const wasLocked = !ozoneExempt && isPlanetOzoneLocked(planet);
    // Economic resources — read cached per-hour rates straight off the planet
    // doc. They are refreshed whenever a building is upgraded (see
    // computePlanetDerivedStats in the upgrade path) so the tick never has to
    // recompute production.
    const metalRate = planet.resources.metal.rate;
    const mineralsRate = planet.resources.minerals.rate;
    const gasRate = planet.resources.gas.rate;
    const metalDelta = wasLocked ? 0 : (metalRate * seconds * resourceMultiplier) / 3600;
    const mineralsDelta = wasLocked ? 0 : (mineralsRate * seconds * resourceMultiplier) / 3600;
    const gasDelta = wasLocked ? 0 : (gasRate * seconds * resourceMultiplier) / 3600;
    const clippedMetal = Math.min(planet.resources.metal.current + metalDelta, planet.resources.metal.capacity);
    const clippedMinerals = Math.min(planet.resources.minerals.current + mineralsDelta, planet.resources.minerals.capacity);
    const clippedGas = Math.min(planet.resources.gas.current + gasDelta, planet.resources.gas.capacity);
    // Energy — income is computed live because it depends on ozone / sun age,
    // both of which change between upgrades. `consumed` is the cumulative
    // draw from every building upgrade on this planet and persists across
    // ticks; the tick only re-derives `current = income − consumed` and
    // never mutates `consumed`. NOT affected by the server multiplier.
    //
    // Formula:
    //   solarUnit       = base_energy / 5
    //   solar_contrib   = solarPlant >= 1 ? solarUnit grown by solarPlant.productionMultipliers : 0
    //   nuclear_contrib = fusionPlant >= 1 ? fusionPlant.baseProduction grown by fusionPlant.productionMultipliers : 0
    //   satellite_contrib = satellite_count × solarUnit / 2
    //
    //   energy = floor( physics_mult × (base_energy + solar + nuclear + satellite_contrib) )
    //
    // Solar scales with sun age + ozone (via base_energy); nuclear is fixed
    // (no ozone / sun-age dependency). Satellites also scale with base_energy
    // but are excluded from the physics multiplier.
    const baseEnergy = calculatePlanetBaseEnergy(sunAgeDays, planet.coordinates.position, planet.state.ozone.current);
    const solarUnit = baseEnergy / 5;
    const solarLevel = planet.buildings.solarPlant?.level ?? 0;
    const nuclearLevel = planet.buildings.fusionPlant?.level ?? 0;
    const satellites = (planet.ships?.[SOLAR_SATELLITE_SHIP_ID] || 0);
    const solarContrib = solarLevel >= 1
        ? growByBreakpoints(solarUnit, BUILDING_DEFINITIONS.solarPlant.productionMultipliers ?? [], solarLevel, true)
        : 0;
    const nuclearContrib = nuclearLevel >= 1
        ? growByBreakpoints(BUILDING_DEFINITIONS.fusionPlant.baseProduction ?? 0, BUILDING_DEFINITIONS.fusionPlant.productionMultipliers ?? [], nuclearLevel, true)
        : 0;
    const satelliteContrib = satellites * solarUnit / 2;
    const energyIncome = Math.floor(calculatePhysicsMultiplier(physicsLevel) *
        (baseEnergy + economyMultiplier * (solarContrib + nuclearContrib + satelliteContrib)));
    const energyConsumed = planet.resources.energy.consumed ?? 0;
    const energyCurrent = energyIncome - energyConsumed;
    // Ozone — net of building-driven loss and Ecology recovery. A Team
    // planet (`ozoneExempt`) is simply held at full, skipping the formula
    // entirely — ozone never affects it either way.
    const updatedOzone = ozoneExempt
        ? OZONE_MAX
        : Math.max(0, planet.state.ozone.current + calculateOzoneChange(planet.buildings, ecologyLevel, seconds));
    return {
        ...planet,
        resources: {
            ...planet.resources,
            metal: { ...planet.resources.metal, current: clippedMetal, rate: metalRate },
            minerals: { ...planet.resources.minerals, current: clippedMinerals, rate: mineralsRate },
            gas: { ...planet.resources.gas, current: clippedGas, rate: gasRate },
            energy: {
                ...planet.resources.energy,
                current: energyCurrent,
                rate: energyIncome,
                consumed: energyConsumed,
            },
            scrap: { ...planet.resources.scrap, current: Math.max(planet.resources.scrap.current, 0) },
        },
        state: {
            ...planet.state,
            ozone: {
                // Kept at full float precision, like metal/minerals/gas above —
                // rounding here (even to a couple of decimal places) would
                // compound error across repeated ticks. Display layers round
                // down (see formatFullNumber) for presentation only.
                current: Math.min(OZONE_MAX, updatedOzone),
            },
            lastTickAt: now,
        },
        updatedAt: now,
    };
}
// ---------------------------------------------------------------------------
// Consumption helpers
// ---------------------------------------------------------------------------
/**
 * Deduct a resource cost upfront (building/ship/research queue start).
 * Economic resources are clamped to 0 — caller must verify affordability
 * first. Energy is tracked cumulatively on `resources.energy.consumed`
 * (the tick derives `current = rate − consumed` from it), so the cost is
 * added to `consumed` rather than deducted from `current`; `current` is
 * refreshed in the same step to keep the returned planet self-consistent
 * before the next tick.
 */
export function applyResourceCost(planet, cost, now) {
    const energyCost = cost.energy ?? 0;
    const newConsumed = (planet.resources.energy.consumed ?? 0) + energyCost;
    return {
        ...planet,
        resources: {
            ...planet.resources,
            metal: { ...planet.resources.metal, current: Math.max(0, planet.resources.metal.current - (cost.metal ?? 0)) },
            minerals: { ...planet.resources.minerals, current: Math.max(0, planet.resources.minerals.current - (cost.minerals ?? 0)) },
            gas: { ...planet.resources.gas, current: Math.max(0, planet.resources.gas.current - (cost.gas ?? 0)) },
            energy: {
                ...planet.resources.energy,
                current: planet.resources.energy.current - energyCost,
                consumed: newConsumed,
            },
        },
        updatedAt: now,
    };
}
/**
 * Refund a paid cost back to the planet at independent rates for the
 * economic resources (metal/minerals/gas) versus Energy. Economic amounts
 * are floored and capped at storage capacity. Energy is released by
 * reducing `consumed` by the refunded amount (bounded at 0), mirroring the
 * additive path in {@link applyResourceCost}; `current` is bumped in the
 * same step to stay consistent until the next tick.
 *
 * Energy gets its own rate because it isn't "spent" the way metal is —
 * metal/minerals/gas are materials invested into the structure and only
 * partially recoverable on cancellation/destruction, while Energy is a
 * live draw tied to the level's existence: once the level is gone (queued
 * and cancelled, or built and destroyed) the draw simply stops, so callers
 * that model Energy this way pass `energyRate = 1`.
 */
export function applyResourceRefundAtRates(planet, cost, economicRate, energyRate, now) {
    const energyRefund = Math.floor((cost.energy ?? 0) * energyRate);
    const prevConsumed = planet.resources.energy.consumed ?? 0;
    const newConsumed = Math.max(0, prevConsumed - energyRefund);
    const appliedEnergyRefund = prevConsumed - newConsumed;
    return {
        ...planet,
        resources: {
            ...planet.resources,
            metal: {
                ...planet.resources.metal,
                current: Math.min(planet.resources.metal.current + Math.floor((cost.metal ?? 0) * economicRate), planet.resources.metal.capacity),
            },
            minerals: {
                ...planet.resources.minerals,
                current: Math.min(planet.resources.minerals.current + Math.floor((cost.minerals ?? 0) * economicRate), planet.resources.minerals.capacity),
            },
            gas: {
                ...planet.resources.gas,
                current: Math.min(planet.resources.gas.current + Math.floor((cost.gas ?? 0) * economicRate), planet.resources.gas.capacity),
            },
            energy: {
                ...planet.resources.energy,
                current: planet.resources.energy.current + appliedEnergyRefund,
                consumed: newConsumed,
            },
        },
        updatedAt: now,
    };
}
/** Refund a fraction of a paid cost back to the planet (queue cancellation) at one uniform rate. */
export function applyResourceRefund(planet, cost, refundType, now) {
    const rate = REFUND_RATES[refundType];
    return applyResourceRefundAtRates(planet, cost, rate, rate, now);
}
// ---------------------------------------------------------------------------
// Premium Resource Pack purchase (`/purchaseResources`) — instantly grants
// RESOURCE_PURCHASE_INCOME_HOURS worth of the planet's OWN current hourly
// income in each of metal/minerals/gas, capped at RESOURCE_PURCHASE_DAILY_LIMIT
// per planet per rolling 24h window. Mirrors the `scrapRecycleDaily` window
// shape (`planScrapRecycleQueueEntry`, `tickEvents.ts`), duplicated here
// rather than shared since a Resource Pack purchase has no queue entry of its
// own to hang the check off — it's a single instantaneous top-up.
// ---------------------------------------------------------------------------
/** Purchases already made in the planet's current rolling 24h window — 0 once the window has expired (or never started). */
export function resourcePurchaseDailyCount(daily, now) {
    if (!daily || daily.resetAt.getTime() <= now.getTime())
        return 0;
    return daily.count;
}
/** Whether another Resource Pack purchase is allowed on this planet right now. */
export function canPurchaseResourcePack(daily, now) {
    return resourcePurchaseDailyCount(daily, now) < RESOURCE_PURCHASE_DAILY_LIMIT;
}
/**
 * Apply a Resource Pack purchase: add `RESOURCE_PURCHASE_INCOME_HOURS` hours
 * worth of the planet's current `rate` — scaled by the universe's
 * `resourceMultiplier`, same as real tick accrual (`calculatePlanetTick`
 * above) — to each of metal/minerals/gas (floored, clamped to storage
 * capacity — same clamp `applyResourceRefundAtRates` uses), and advance the
 * rolling 24h purchase-count window. Callers must check
 * `canPurchaseResourcePack` first — this does not re-check the cap. Throws
 * `failed-precondition` if every resource is already at capacity — paying
 * Dark Matter for a pack that credits nothing is never valid, same guard
 * `planResourceTrade` applies to a fully-wasted trade.
 */
export function applyResourcePurchase(planet, now, resourceMultiplier = 1) {
    const dailyCount = resourcePurchaseDailyCount(planet.resourcePurchaseDaily, now);
    const windowExpired = !planet.resourcePurchaseDaily || planet.resourcePurchaseDaily.resetAt.getTime() <= now.getTime();
    const grant = (key) => {
        const resource = planet.resources[key];
        const nextCurrent = Math.min(resource.current + Math.floor(resource.rate * RESOURCE_PURCHASE_INCOME_HOURS * resourceMultiplier), resource.capacity);
        return { resource: { ...resource, current: nextCurrent }, credited: nextCurrent - resource.current };
    };
    const metal = grant('metal');
    const minerals = grant('minerals');
    const gas = grant('gas');
    if (metal.credited <= 0 && minerals.credited <= 0 && gas.credited <= 0) {
        throw new QueueError("failed-precondition", "Storage is already full — this Resource Pack would grant nothing");
    }
    return {
        ...planet,
        resources: {
            ...planet.resources,
            metal: metal.resource,
            minerals: minerals.resource,
            gas: gas.resource,
        },
        resourcePurchaseDaily: {
            count: dailyCount + 1,
            resetAt: windowExpired ? new Date(now.getTime() + 24 * 3600 * 1000) : planet.resourcePurchaseDaily.resetAt,
        },
        updatedAt: now,
    };
}
//# sourceMappingURL=resourceEngine.js.map